<template>
  <v-navigation-drawer
    id="default-drawer"
    v-model="drawer"
    color="white"
    light
    :right="$vuetify.rtl"
    :src="drawerImage ? image : ''"
    :mini-variant.sync="mini"
    mini-variant-width="80"
    app
    width="260"
  >
    <div class="">      
      <default-drawer-header
        class="px-2"
        :style="`background-color: ${$route.name === 'Multiverse' ? '#009688' : '#F47C20'}`"
      />
      <v-list
        nav
        shaped
      >
        <v-list-item
          to="/"
          active-class="grey lighten-2 black--text"
          link
          class="py-1 mt-4"
        >
          <v-list-item-icon>
            <v-icon>$vuetify.icons.home</v-icon>
          </v-list-item-icon>
          <v-list-item-title>
            Central Hub
          </v-list-item-title>
        </v-list-item>
      </v-list>
      <is-dev toggle>
        <v-subheader>
          Dev
        </v-subheader>
        <v-divider />
        <v-list-item
          to="/ui/developer/settings"
          active-class="grey lighten-2 black--text"
          link
          class="py-1"
        >
          <v-list-item-icon>
            <v-icon>$vuetify.icons.universe</v-icon>
          </v-list-item-icon>
          <v-list-item-title>
            Developer settings
          </v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="ideStatus === 'running'"
          active-class="grey lighten-2 black--text"
          link
          class="py-1"
          :href="ideUrl"
          target="_blank"
        >
          <v-list-item-icon>
            <v-icon>mdi-microsoft-visual-studio-code</v-icon>
          </v-list-item-icon>
          <v-list-item-title>
            IDE
          </v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="notebookStatus === 'running'"          
          active-class="grey lighten-2 black--text"
          link
          class="py-1"
          :href="notebookUrl"
          target="_blank"
        >
          <v-list-item-icon>
            <v-icon>$jupyter</v-icon>
          </v-list-item-icon>
          <v-list-item-title>
            Notebook
          </v-list-item-title>
        </v-list-item>
      </is-dev>
      <v-subheader>
        Universes
      </v-subheader>
      <v-divider />
      <default-list :items="names" />
    </div>
    <div class="pt-12" />    
  </v-navigation-drawer>
</template>

<script>
  // Utilities
  import { get, sync } from 'vuex-pathify'
  import { mapState, mapActions } from 'pinia'
  import { useUniverseStore } from '@/store/universe-store'  

  export default {
    name: 'DefaultDrawer',
    components: {
      DefaultDrawerHeader: () => import(
        /* webpackChunkName: "default-drawer-header" */
        './widgets/DrawerHeader'
      ),
      DefaultList: () => import(
        /* webpackChunkName: "default-list" */
        './List'
      ),
    },
    data: () => {
      return {
        names: [],
        ideInterval: null,
        notebookInterval: null,
      }
    },
    computed: {      
      ...mapState(useUniverseStore, ['universeNames']),
      ...get('user', [
        'dark',
        'gradient',
        'image',
      ]),
      ...get('app', [
        'items',
        'version',
      ]),
      ...sync('app', [
        'drawer',
        'drawerImage',
        'mini',
      ]),
      ideStatus: {
        get () {
          return this.$store.state.app.ideStatus
        },
        set (v) {
          this.$store.commit('app/ideStatus', v)
        },
      },
      notebookStatus: {
        get () {
          return this.$store.state.app.notebookStatus
        },
        set (v) {
          this.$store.commit('app/notebookStatus', v)
        },
      },
      userInfo () {
        return this.parseJwt(this.$cookies.get('token'))
      },
      rfcUsername () {
        const username = this.userInfo?.preferred_username || ''
        return username.replaceAll(/[^a-zA-Z\d]/g, '')
      },
      ideUrl () {
        return `https://ide-${this.rfcUsername}.${document.location.host}`
      },
      notebookUrl () {
        return `https://notebook-${this.rfcUsername}.${document.location.host}`
      },
    },
    watch: {
      universeNames (v) {
        this.names = this.universeNames.map(name => ({
            title: name.id,
            to: `/ui/universe/${name.id}/apps`,
            icon: '$vuetify.icons.universe',
          }))
      },
    },
    async mounted () {
      await this.loadUniverseNames()
      this.resetIdeInterval()
      this.resetNotebookInterval()
    },
    unmounted () {
      clearInterval(this.ideInterval)
      clearInterval(this.notebookInterval)
    },
    beforeRouteLeave (to, from, next) {
      clearInterval(this.ideInterval)
      clearInterval(this.notebookInterval)
      next()
    },
    beforeDestroy () {
      clearInterval(this.ideInterval)
      clearInterval(this.notebookInterval)
    },
    deactivated () {
      clearInterval(this.ideInterval)
      clearInterval(this.notebookInterval)
    },
    methods: {
      ...mapActions(useUniverseStore, ['loadUniverseNames']),
      parseJwt (token) {
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(c => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        }).join(''))
        return JSON.parse(jsonPayload)
      },
      resetIdeInterval (skipLoad) {
        if (!skipLoad) {
            this.$store.dispatch('app/getIdeStatus')
        }
        if (this.ideInterval) {
            clearInterval(this.ideInterval)
        }
        this.ideInterval = setInterval(() => {
            this.$store.dispatch('app/getIdeStatus')
        }, 5000)
      },
      resetNotebookInterval (skipLoad) {
          if (!skipLoad) {
              this.$store.dispatch('app/getNotebookStatus')
          }
          if (this.ideInterval) {
              clearInterval(this.ideInterval)
          }
          this.ideInterval = setInterval(() => {
              this.$store.dispatch('app/getNotebookStatus')
          }, 5250)
      },
    },
  }
</script>

<style lang="sass">
#default-drawer
  .v-list-item
    margin-bottom: 8px

  .v-list-item::before,
  .v-list-item::after
    display: none

  .v-list-group__header__prepend-icon,
  .v-list-item__icon
    margin-top: 12px
    margin-bottom: 12px
    margin-left: 4px

  &.v-navigation-drawer--mini-variant
    .v-list-item
      justify-content: flex-start !important
</style>
